/**
 * Dynamic router for API endpoints
 */

import type { RouteContext, ApiConfig } from './types';
import { tasksHandler } from './routes/tasks';
import { filesHandler } from './routes/files';
import { healthHandler } from './routes/health';
import { streamHandler } from './routes/stream';

export interface Route {
  method: string;
  path: string;
  handler: (context: RouteContext) => Promise<any>;
}

export class ApiRouter {
  private routes: Route[] = [];

  constructor(config: ApiConfig) {
    this.setupRoutes(config);
  }

  private setupRoutes(config: ApiConfig) {
    const { features = {} } = config;

    // Health check (always enabled)
    this.addRoute('GET', '/health', healthHandler);

    // Tasks API
    if (features.tasks !== false) {
      this.addRoute('GET', '/tasks', tasksHandler);
      this.addRoute('POST', '/tasks', tasksHandler);
    }

    // Files API
    if (features.files !== false) {
      this.addRoute('GET', '/files', filesHandler);
      this.addRoute('POST', '/files', filesHandler);
      this.addRoute('PUT', '/files', filesHandler);
      this.addRoute('DELETE', '/files', filesHandler);
    }

    // Streaming endpoints
    if (features.realtime !== false) {
      this.addRoute('GET', '/tasks/stream', streamHandler);
    }

    // Root endpoint - API info
    this.addRoute('GET', '/', this.rootHandler);
  }

  private addRoute(method: string, path: string, handler: (context: RouteContext) => Promise<any>) {
    this.routes.push({ method, path, handler });
  }

  async handle(context: RouteContext): Promise<any> {
    const { request } = context;
    const method = request.method;
    const path = this.extractPath(request, context.basePath);

    // Find matching route
    for (const route of this.routes) {
      if (this.matchRoute(route, method, path)) {
        return await route.handler(context);
      }
    }

    return null; // No route matched
  }

  private extractPath(request: Request, basePath: string): string {
    const url = new URL(request.url);
    const fullPath = url.pathname;
    
    if (fullPath.startsWith(basePath)) {
      return fullPath.slice(basePath.length) || '/';
    }
    
    return fullPath;
  }

  private matchRoute(route: Route, method: string, path: string): boolean {
    if (route.method !== method) {
      return false;
    }

    // Exact match
    if (route.path === path) {
      return true;
    }

    // Path parameters (simple implementation)
    const routeParts = route.path.split('/');
    const pathParts = path.split('/');

    if (routeParts.length !== pathParts.length) {
      return false;
    }

    for (let i = 0; i < routeParts.length; i++) {
      const routePart = routeParts[i];
      const pathPart = pathParts[i];

      // Parameter (starts with :)
      if (routePart.startsWith(':')) {
        continue;
      }

      // Exact match required
      if (routePart !== pathPart) {
        return false;
      }
    }

    return true;
  }

  private async rootHandler(context: RouteContext): Promise<any> {
    return {
      name: 'Vibeship DevTools API',
      version: '1.1.0',
      endpoints: this.routes.map(route => ({
        method: route.method,
        path: route.path,
      })),
      features: context.config.features,
      documentation: 'https://vibeship.dev/docs/api',
    };
  }
}

/**
 * Create a router instance
 */
export function createRouter(config: ApiConfig): ApiRouter {
  return new ApiRouter(config);
}